import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart } from 'recharts';
import { Building2, ShoppingCart, Package, Truck, ClipboardList, TrendingUp, DollarSign, Calendar, Clock, Settings, AlertTriangle, CheckCircle, XCircle, Eye, MapPin, User, Bell } from 'lucide-react';
import logo from '../assets/infraLogo.jpg';
import LayoutWrapper from '../components/LayoutWrapper';
const InfraDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showSkinDropdown, setShowSkinDropdown] = useState(false);
  const [selectedSkin, setSelectedSkin] = useState('blue');

  // Skin/Theme configurations with full Tailwind class names
  const skins = {
    blue: {
      name: 'Corporate Blue',
      bgClasses: 'bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50',
      bgOverlay1Classes: 'bg-gradient-to-br from-blue-100/40 via-sky-50/60 to-indigo-100/40',
      bgOverlay2Classes: 'bg-gradient-to-tr from-cyan-50/30 via-transparent to-blue-100/30',
      orbClasses: [
        'bg-blue-200/20',
        'bg-indigo-200/15',
        'bg-sky-200/20',
        'bg-cyan-200/15'
      ],
      preview: 'bg-gradient-to-r from-blue-500 to-indigo-500',
      headerBgClasses: 'bg-gradient-to-r from-blue-50/30 via-transparent to-indigo-50/30',
      headerDotClasses: ['bg-blue-400', 'bg-indigo-400', 'bg-purple-400']
    },
    green: {
      name: 'Nature Green',
      bgClasses: 'bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50',
      bgOverlay1Classes: 'bg-gradient-to-br from-green-100/40 via-emerald-50/60 to-teal-100/40',
      bgOverlay2Classes: 'bg-gradient-to-tr from-lime-50/30 via-transparent to-green-100/30',
      orbClasses: [
        'bg-green-200/20',
        'bg-emerald-200/15',
        'bg-teal-200/20',
        'bg-lime-200/15'
      ],
      preview: 'bg-gradient-to-r from-green-500 to-emerald-500',
      headerBgClasses: 'bg-gradient-to-r from-green-50/30 via-transparent to-emerald-50/30',
      headerDotClasses: ['bg-green-400', 'bg-emerald-400', 'bg-teal-400']
    },
    purple: {
      name: 'Royal Purple',
      bgClasses: 'bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50',
      bgOverlay1Classes: 'bg-gradient-to-br from-purple-100/40 via-violet-50/60 to-indigo-100/40',
      bgOverlay2Classes: 'bg-gradient-to-tr from-fuchsia-50/30 via-transparent to-purple-100/30',
      orbClasses: [
        'bg-purple-200/20',
        'bg-violet-200/15',
        'bg-indigo-200/20',
        'bg-fuchsia-200/15'
      ],
      preview: 'bg-gradient-to-r from-purple-500 to-violet-500',
      headerBgClasses: 'bg-gradient-to-r from-purple-50/30 via-transparent to-violet-50/30',
      headerDotClasses: ['bg-purple-400', 'bg-violet-400', 'bg-fuchsia-400']
    },
    orange: {
      name: 'Sunset Orange',
      bgClasses: 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50',
      bgOverlay1Classes: 'bg-gradient-to-br from-orange-100/40 via-amber-50/60 to-yellow-100/40',
      bgOverlay2Classes: 'bg-gradient-to-tr from-red-50/30 via-transparent to-orange-100/30',
      orbClasses: [
        'bg-orange-200/20',
        'bg-amber-200/15',
        'bg-yellow-200/20',
        'bg-red-200/15'
      ],
      preview: 'bg-gradient-to-r from-orange-500 to-amber-500',
      headerBgClasses: 'bg-gradient-to-r from-orange-50/30 via-transparent to-amber-50/30',
      headerDotClasses: ['bg-orange-400', 'bg-amber-400', 'bg-red-400']
    },
    gray: {
      name: 'Professional Gray',
      bgClasses: 'bg-gradient-to-br from-gray-50 via-slate-50 to-zinc-50',
      bgOverlay1Classes: 'bg-gradient-to-br from-gray-100/40 via-slate-50/60 to-zinc-100/40',
      bgOverlay2Classes: 'bg-gradient-to-tr from-neutral-50/30 via-transparent to-gray-100/30',
      orbClasses: [
        'bg-gray-200/20',
        'bg-slate-200/15',
        'bg-zinc-200/20',
        'bg-neutral-200/15'
      ],
      preview: 'bg-gradient-to-r from-gray-500 to-slate-500',
      headerBgClasses: 'bg-gradient-to-r from-gray-50/30 via-transparent to-slate-50/30',
      headerDotClasses: ['bg-gray-400', 'bg-slate-400', 'bg-zinc-400']
    },
    rose: {
      name: 'Elegant Rose',
      bgClasses: 'bg-gradient-to-br from-rose-50 via-pink-50 to-red-50',
      bgOverlay1Classes: 'bg-gradient-to-br from-rose-100/40 via-pink-50/60 to-red-100/40',
      bgOverlay2Classes: 'bg-gradient-to-tr from-fuchsia-50/30 via-transparent to-rose-100/30',
      orbClasses: [
        'bg-rose-200/20',
        'bg-pink-200/15',
        'bg-red-200/20',
        'bg-fuchsia-200/15'
      ],
      preview: 'bg-gradient-to-r from-rose-500 to-pink-500',
      headerBgClasses: 'bg-gradient-to-r from-rose-50/30 via-transparent to-pink-50/30',
      headerDotClasses: ['bg-rose-400', 'bg-pink-400', 'bg-fuchsia-400']
    }
  };



  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSkinDropdown && !event.target.closest('.skin-dropdown-container')) {
        setShowSkinDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showSkinDropdown]);

  // Enhanced sample data
  const purchasingData = [
    { month: 'Jan', amount: 45000, category: 'Materials', budget: 50000 },
    { month: 'Feb', amount: 52000, category: 'Materials', budget: 55000 },
    { month: 'Mar', amount: 38000, category: 'Equipment', budget: 45000 },
    { month: 'Apr', amount: 61000, category: 'Materials', budget: 60000 },
    { month: 'May', amount: 49000, category: 'Equipment', budget: 52000 },
    { month: 'Jun', amount: 71000, category: 'Materials', budget: 70000 }
  ];

  const orderSummaryData = [
    { name: 'Completed', value: 342, color: '#10B981' },
    { name: 'In Progress', value: 128, color: '#F59E0B' },
    { name: 'Pending', value: 89, color: '#EF4444' },
    { name: 'Cancelled', value: 23, color: '#6B7280' }
  ];

  const vehicleData = [
    { id: 'EXC-001', type: 'Excavator CAT 320', status: 'Active', location: 'Site A', operator: 'John Smith', lastMaintenance: '2024-07-15', nextMaintenance: '2024-08-15', fuelLevel: 85, hoursWorked: 1247 },
    { id: 'BUL-002', type: 'Bulldozer D6T', status: 'Active', location: 'Site B', operator: 'Mike Johnson', lastMaintenance: '2024-07-10', nextMaintenance: '2024-08-10', fuelLevel: 72, hoursWorked: 892 },
    { id: 'CRN-003', type: 'Mobile Crane 50T', status: 'Maintenance', location: 'Workshop', operator: 'David Brown', lastMaintenance: '2024-07-20', nextMaintenance: '2024-07-28', fuelLevel: 45, hoursWorked: 1567 },
    { id: 'TRK-004', type: 'Dump Truck 20T', status: 'Active', location: 'Site C', operator: 'Sarah Wilson', lastMaintenance: '2024-07-05', nextMaintenance: '2024-08-05', fuelLevel: 91, hoursWorked: 734 },
    { id: 'MIX-005', type: 'Concrete Mixer', status: 'Active', location: 'Site A', operator: 'Tom Anderson', lastMaintenance: '2024-07-12', nextMaintenance: '2024-08-12', fuelLevel: 67, hoursWorked: 456 },
    { id: 'LOD-006', type: 'Wheel Loader', status: 'Inactive', location: 'Depot', operator: 'Lisa Garcia', lastMaintenance: '2024-07-18', nextMaintenance: '2024-08-18', fuelLevel: 23, hoursWorked: 1123 }
  ];

  const inventoryData = [
    { item: 'Cement', quantity: 450, unit: 'Bags', threshold: 100, cost: 12, supplier: 'Portland Cement Co.', lastOrder: '2024-07-20', status: 'In Stock' },
    { item: 'Steel Bars', quantity: 2800, unit: 'Tons', threshold: 500, cost: 850, supplier: 'Steel Industries Ltd.', lastOrder: '2024-07-18', status: 'In Stock' },
    { item: 'Bricks', quantity: 15000, unit: 'Pieces', threshold: 5000, cost: 0.45, supplier: 'Brick Masters', lastOrder: '2024-07-22', status: 'In Stock' },
    { item: 'Sand', quantity: 45, unit: 'Cubic Meters', threshold: 50, cost: 35, supplier: 'River Sand Co.', lastOrder: '2024-07-15', status: 'Low Stock' },
    { item: 'Gravel', quantity: 220, unit: 'Cubic Meters', threshold: 60, cost: 42, supplier: 'Aggregate Plus', lastOrder: '2024-07-19', status: 'In Stock' },
    { item: 'Paint', quantity: 25, unit: 'Gallons', threshold: 30, cost: 65, supplier: 'Color Pro', lastOrder: '2024-07-10', status: 'Low Stock' }
  ];

  const deliveryData = [
    { week: 'Week 1', delivered: 95, scheduled: 100, onTime: 92 },
    { week: 'Week 2', delivered: 87, scheduled: 90, onTime: 85 },
    { week: 'Week 3', delivered: 78, scheduled: 85, onTime: 76 },
    { week: 'Week 4', delivered: 92, scheduled: 95, onTime: 89 },
    { week: 'Week 5', delivered: 89, scheduled: 88, onTime: 87 },
    { week: 'Week 6', delivered: 94, scheduled: 96, onTime: 92 }
  ];

  const recentDeliveries = [
    { id: 'DEL-2024-001', items: 'Cement Bags (50)', destination: 'Mall Construction Site', status: 'Delivered', date: '2024-07-28', driver: 'Carlos Rodriguez', estimatedTime: '09:30 AM', actualTime: '09:15 AM' },
    { id: 'DEL-2024-002', items: 'Steel Rods (20 tons)', destination: 'Office Building', status: 'In Transit', date: '2024-07-29', driver: 'James Wilson', estimatedTime: '02:00 PM', actualTime: '-' },
    { id: 'DEL-2024-003', items: 'Bricks (5000)', destination: 'Residential Complex', status: 'Scheduled', date: '2024-07-30', driver: 'Maria Santos', estimatedTime: '11:00 AM', actualTime: '-' },
    { id: 'DEL-2024-004', items: 'Concrete Mix', destination: 'Highway Bridge', status: 'Delivered', date: '2024-07-27', driver: 'Robert Lee', estimatedTime: '08:00 AM', actualTime: '07:45 AM' }
  ];

  const COLORS = ['#10B981', '#F59E0B', '#EF4444', '#6B7280', '#8B5CF6', '#06B6D4'];

  const StatCard = ({ title, value, icon: Icon, color, change, subtitle }) => (
    <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          {change && (
            <div className={`flex items-center mt-2 ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
              <TrendingUp className="h-4 w-4 mr-1" />
              <span className="text-sm font-semibold">
                {change > 0 ? '+' : ''}{change}% from last month
              </span>
            </div>
          )}
        </div>
        <div className="p-4 rounded-2xl" style={{ backgroundColor: color + '15' }}>
          <Icon className="h-8 w-8" style={{ color }} />
        </div>
      </div>
    </div>
  );
    const currentSkin = {
    bgClasses: 'bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50',
    bgOverlay1Classes: 'bg-gradient-to-br from-blue-100/40 via-sky-50/60 to-indigo-100/40',
    bgOverlay2Classes: 'bg-gradient-to-tr from-cyan-50/30 via-transparent to-blue-100/30',
    orbClasses: [
      'bg-blue-200/20',
      'bg-indigo-200/15',
      'bg-sky-200/20',
      'bg-cyan-200/15'
    ],
    headerBgClasses: 'bg-gradient-to-r from-blue-50/30 via-transparent to-indigo-50/30',
    headerDotClasses: ['bg-blue-400', 'bg-indigo-400', 'bg-purple-400']
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active':
      case 'Delivered':
      case 'In Stock':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'Maintenance':
      case 'In Transit':
      case 'Low Stock':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'Inactive':
      case 'Cancelled':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-blue-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
      case 'Delivered':
      case 'In Stock':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Maintenance':
      case 'In Transit':
      case 'Low Stock':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Inactive':
      case 'Cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  return (
    <LayoutWrapper>
    <div className={`min-h-screen ${currentSkin.bgClasses} relative overflow-hidden`}>
      {/* Dynamic Corporate Light Gradient Background */}
      <div className={`absolute inset-0 ${currentSkin.bgOverlay1Classes}`}></div>
      <div className={`absolute inset-0 ${currentSkin.bgOverlay2Classes}`}></div>
      
      {/* Dynamic animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-20 left-10 w-32 h-32 ${currentSkin.orbClasses[0]} rounded-full blur-2xl animate-pulse`}></div>
        <div className={`absolute top-40 right-20 w-48 h-48 ${currentSkin.orbClasses[1]} rounded-full blur-3xl animate-pulse delay-1000`}></div>
        <div className={`absolute bottom-32 left-1/3 w-40 h-40 ${currentSkin.orbClasses[2]} rounded-full blur-2xl animate-pulse delay-500`}></div>
        <div className={`absolute bottom-20 right-1/4 w-36 h-36 ${currentSkin.orbClasses[3]} rounded-full blur-3xl animate-pulse delay-700`}></div>
      </div>
      
      {/* Main Content Wrapper */}
      <div className="relative z-10">
        {/* Premium Enhanced Header */}
        <header className="bg-white/85 backdrop-blur-xl shadow-2xl border-b border-gradient-to-r from-blue-200/50 to-indigo-200/50 sticky top-0 z-50 relative overflow-hidden">
          {/* Subtle animated background pattern */}
          <div className={`absolute inset-0 ${currentSkin.headerBgClasses}`}></div>
          <div className="absolute inset-0 opacity-30">
            <div className={`absolute top-0 left-1/4 w-2 h-2 ${currentSkin.headerDotClasses[0]} rounded-full animate-pulse`}></div>
            <div className={`absolute top-3 right-1/3 w-1 h-1 ${currentSkin.headerDotClasses[1]} rounded-full animate-pulse delay-1000`}></div>
            <div className={`absolute bottom-2 left-2/3 w-1.5 h-1.5 ${currentSkin.headerDotClasses[2]} rounded-full animate-pulse delay-500`}></div>
          </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex justify-between items-center py-5">
            {/* Enhanced Logo Section */}
            <div className="flex items-center group">
              {/* Premium Logo Container */}
              <div className="relative mr-4">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur-lg opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
                <div className="relative p-3 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 rounded-2xl shadow-xl transform group-hover:scale-105 transition-all duration-300">
                  {/* Custom Logo Design */}
                  <div className="relative w-9 h-9 flex items-center justify-center">
                    <img 
                      src={logo}
                      alt="Infraconsoft Logo" 
                      className="w-8 h-8 object-contain rounded-lg"
                      onError={(e) => {
                        // Fallback if image doesn't load
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    {/* Fallback icon */}
                    <Building2 className="h-8 w-8 text-white hidden" />
                    
                    {/* Logo accent dots */}
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full shadow-lg"></div>
                    <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full shadow-lg"></div>
                  </div>
                </div>
              </div>
              
              {/* Enhanced Brand Text */}
              <div className="relative">
                <h1 className="text-3xl font-black bg-gradient-to-r from-blue-700 via-indigo-600 to-purple-600 bg-clip-text text-transparent tracking-tight">
                  Infraconsoft
                </h1>
                <div className="flex items-center space-x-2 mt-1">
                  <p className="text-sm font-medium text-gray-600">Construction Management Platform</p>
                  <div className="px-2 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 text-xs font-bold rounded-full border border-green-200">
                    Pro
                  </div>
                </div>
                {/* Subtle underline animation */}
                <div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
            </div>

            {/* Enhanced Action Bar */}
            <div className="flex items-center space-x-4">
              {/* Search Bar */}
              <div className="hidden md:flex items-center bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl px-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300">
                <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input 
                  type="text" 
                  placeholder="Search projects..." 
                  className="bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-400 w-32 lg:w-48"
                />
              </div>

              {/* Notification Bell */}
              <div className="relative">
                <button className="p-3 rounded-2xl bg-white/70 backdrop-blur-sm border border-gray-200/50 hover:bg-white/90 hover:shadow-xl transition-all duration-300 group">
                  <Bell className="h-5 w-5 text-gray-600 group-hover:text-blue-600 transition-colors" />
                  {/* Notification badge */}
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-xs font-bold text-white">3</span>
                  </div>
                </button>
              </div>

           

              {/* Settings */}
              <button className="p-3 rounded-2xl bg-white/70 backdrop-blur-sm border border-gray-200/50 hover:bg-white/90 hover:shadow-xl transition-all duration-300 group">
                <Settings className="h-5 w-5 text-gray-600 group-hover:text-blue-600 transition-colors" />
              </button>

              {/* Enhanced User Profile */}
              <div className="flex items-center space-x-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl px-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer">
                {/* User Avatar with Status */}
                <div className="relative">
                  <div className="w-11 h-11 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  {/* Online status indicator */}
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full border-2 border-white shadow-lg"></div>
                </div>
                
                {/* User Info */}
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                    Alex Johnson
                  </p>
                  <div className="flex items-center space-x-1">
                    <p className="text-xs text-gray-600">Project Manager</p>
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <p className="text-xs text-green-600 font-medium">Online</p>
                  </div>
                </div>

                {/* Dropdown Arrow */}
                <svg className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Navigation Tabs */}
          <div className="mb-8">
            <nav className="flex space-x-2 bg-white/60 backdrop-blur-sm p-2 rounded-2xl border border-gray-200/50">
              {[
                { id: 'overview', name: 'Overview', icon: TrendingUp },
                { id: 'purchasing', name: 'Purchasing', icon: DollarSign },
                { id: 'orders', name: 'Order Summary', icon: ClipboardList },
                { id: 'vehicles', name: 'Vehicle Details', icon: Truck },
                { id: 'inventory', name: 'Inventory', icon: Package },
                { id: 'delivery', name: 'Order & Delivery', icon: Calendar }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/80'
                  }`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        {/* Overview Dashboard */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Enhanced Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Projects"
                value="47"
                subtitle="8 active, 39 completed"
                icon={Building2}
                color="#10B981"
                change={12}
              />
              <StatCard
                title="Active Orders"
                value="128"
                subtitle="89 pending approval"
                icon={ShoppingCart}
                color="#F59E0B"
                change={-3}
              />
              <StatCard
                title="Fleet Vehicles"
                value="61"
                subtitle="55 operational"
                icon={Truck}
                color="#8B5CF6"
                change={8}
              />
              <StatCard
                title="Monthly Revenue"
                value="$2.4M"
                subtitle="Q2 target: $2.8M"
                icon={DollarSign}
                color="#06B6D4"
                change={15}
              />
            </div>

            {/* Enhanced Overview Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Monthly Purchasing Trends</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={purchasingData}>
                    <defs>
                      <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Amount']}
                      contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }}
                    />
                    <Area type="monotone" dataKey="amount" stroke="#10B981" fillOpacity={1} fill="url(#colorAmount)" strokeWidth={3} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Order Status Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={orderSummaryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      stroke="none"
                    >
                      {orderSummaryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Purchasing Tab */}
        {activeTab === 'purchasing' && (
          <div className="space-y-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Purchasing Analytics</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={purchasingData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip 
                    formatter={(value, name) => [`$${value.toLocaleString()}`, name === 'amount' ? 'Spent' : 'Budget']}
                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }}
                  />
                  <Legend />
                  <Bar dataKey="amount" fill="#10B981" radius={[4, 4, 0, 0]} name="Amount Spent" />
                  <Bar dataKey="budget" fill="#E5E7EB" radius={[4, 4, 0, 0]} name="Budget" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Top Categories</h4>
                <div className="space-y-4">
                  {[
                    { name: 'Construction Materials', amount: 245000, percentage: 45 },
                    { name: 'Heavy Equipment', amount: 187000, percentage: 34 },
                    { name: 'Tools & Supplies', amount: 98000, percentage: 18 }
                  ].map((category, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700 font-medium">{category.name}</span>
                        <span className="font-bold text-gray-900">${(category.amount / 1000).toFixed(0)}K</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-500" 
                          style={{width: `${category.percentage}%`}}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Budget Status</h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-medium">Materials Budget</span>
                      <span className="font-bold">75%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500" style={{width: '75%'}}></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-600 mt-1">
                      <span>$187.5K used</span>
                      <span>$250K total</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-medium">Equipment Budget</span>
                      <span className="font-bold">60%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500" style={{width: '60%'}}></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-600 mt-1">
                      <span>$120K used</span>
                      <span>$200K total</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Recent Orders</h4>
                <div className="space-y-3">
                  {[
                    { item: 'Cement Bags - 200 units', status: 'Delivered Today', color: 'green' },
                    { item: 'Steel Rods - 50 tons', status: 'In Transit', color: 'yellow' },
                    { item: 'Excavator Rental', status: 'Pending Approval', color: 'blue' }
                  ].map((order, index) => (
                    <div key={index} className={`border-l-4 ${order.color === 'green' ? 'border-green-500 bg-green-50/50' : order.color === 'yellow' ? 'border-yellow-500 bg-yellow-50/50' : 'border-blue-500 bg-blue-50/50'} pl-4 py-2 rounded-r-lg`}>
                      <p className="font-medium text-gray-900">{order.item}</p>
                      <p className="text-sm text-gray-600">{order.status}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Order Summary Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Order Status Distribution</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={orderSummaryData}
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                      stroke="none"
                    >
                      {orderSummaryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Order Statistics</h3>
                <div className="space-y-6">
                  {orderSummaryData.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl">
                      <div className="flex items-center">
                        <div 
                          className="w-4 h-4 rounded-full mr-4"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className="text-gray-700 font-medium">{item.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">{item.value}</p>
                        <p className="text-sm text-gray-600">orders</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Recent Order Activity</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Order ID</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Items</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Destination</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recentDeliveries.map((delivery, index) => (
                      <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{delivery.id}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{delivery.items}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{delivery.destination}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(delivery.status)}`}>
                            {getStatusIcon(delivery.status)}
                            <span className="ml-1">{delivery.status}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{delivery.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Vehicle Details Tab */}
        {activeTab === 'vehicles' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <StatCard
                title="Total Vehicles"
                value="61"
                subtitle="Fleet capacity"
                icon={Truck}
                color="#8B5CF6"
              />
              <StatCard
                title="Active Vehicles"
                value="55"
                subtitle="Currently operational"
                icon={CheckCircle}
                color="#10B981"
              />
              <StatCard
                title="In Maintenance"
                value="4"
                subtitle="Service required"
                icon={Settings}
                color="#F59E0B"
              />
              <StatCard
                title="Fuel Efficiency"
                value="87%"
                subtitle="Average across fleet"
                icon={TrendingUp}
                color="#06B6D4"
              />
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Vehicle Fleet Management</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Vehicle ID</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Type</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Location</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Operator</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Fuel Level</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Hours Worked</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Next Maintenance</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {vehicleData.map((vehicle, index) => (
                      <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{vehicle.id}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{vehicle.type}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(vehicle.status)}`}>
                            {getStatusIcon(vehicle.status)}
                            <span className="ml-1">{vehicle.status}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                            {vehicle.location}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{vehicle.operator}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${vehicle.fuelLevel > 70 ? 'bg-green-500' : vehicle.fuelLevel > 30 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{width: `${vehicle.fuelLevel}%`}}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-600">{vehicle.fuelLevel}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{vehicle.hoursWorked}h</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{vehicle.nextMaintenance}</td>
                        <td className="px-6 py-4">
                          <button className="text-blue-600 hover:text-blue-800 transition-colors">
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Vehicle Status Overview</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Active', value: 55, color: '#10B981' },
                        { name: 'Maintenance', value: 4, color: '#F59E0B' },
                        { name: 'Inactive', value: 2, color: '#EF4444' }
                      ]}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                      stroke="none"
                    >
                      {[
                        { name: 'Active', value: 55, color: '#10B981' },
                        { name: 'Maintenance', value: 4, color: '#F59E0B' },
                        { name: 'Inactive', value: 2, color: '#EF4444' }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Maintenance Schedule</h4>
                <div className="space-y-4">
                  {vehicleData
                    .filter(vehicle => new Date(vehicle.nextMaintenance) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))
                    .map((vehicle, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                      <div>
                        <p className="font-medium text-gray-900">{vehicle.type}</p>
                        <p className="text-sm text-gray-600">{vehicle.id}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-yellow-800">{vehicle.nextMaintenance}</p>
                        <p className="text-xs text-yellow-600">Due soon</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Inventory Tab */}
        {activeTab === 'inventory' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <StatCard
                title="Total Items"
                value={inventoryData.length.toString()}
                subtitle="In warehouse"
                icon={Package}
                color="#8B5CF6"
              />
              <StatCard
                title="Low Stock Items"
                value={inventoryData.filter(item => item.quantity <= item.threshold).length.toString()}
                subtitle="Need restocking"
                icon={AlertTriangle}
                color="#F59E0B"
              />
              <StatCard
                title="Total Value"
                value={`$${inventoryData.reduce((sum, item) => sum + (item.quantity * item.cost), 0).toLocaleString()}`}
                subtitle="Current inventory"
                icon={DollarSign}
                color="#10B981"
              />
              <StatCard
                title="Suppliers"
                value="12"
                subtitle="Active partners"
                icon={Building2}
                color="#06B6D4"
              />
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Inventory Management</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Item</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Quantity</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Unit Cost</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Total Value</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Supplier</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Last Order</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {inventoryData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{item.item}</p>
                            <p className="text-xs text-gray-500">Threshold: {item.threshold} {item.unit}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {item.quantity} {item.unit}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">${item.cost}</td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          ${(item.quantity * item.cost).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{item.supplier}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{item.lastOrder}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(item.status)}`}>
                            {getStatusIcon(item.status)}
                            <span className="ml-1">{item.status}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button className="text-blue-600 hover:text-blue-800 transition-colors mr-2">
                            <Eye className="h-4 w-4" />
                          </button>
                          {item.quantity <= item.threshold && (
                            <button className="text-green-600 hover:text-green-800 transition-colors">
                              <ShoppingCart className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Stock Level Analysis</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={inventoryData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="item" stroke="#6b7280" angle={-45} textAnchor="end" height={80} />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      formatter={(value, name) => [value, name === 'quantity' ? 'Current Stock' : 'Threshold']}
                      contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }}
                    />
                    <Bar dataKey="quantity" fill="#10B981" radius={[4, 4, 0, 0]} name="Current Stock" />
                    <Bar dataKey="threshold" fill="#EF4444" radius={[4, 4, 0, 0]} name="Minimum Threshold" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h4>
                <div className="space-y-4">
                  <button className="w-full p-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-105">
                    Generate Purchase Orders
                  </button>
                  <button className="w-full p-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 transform hover:scale-105">
                    Update Stock Levels
                  </button>
                  <button className="w-full p-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105">
                    Export Inventory Report
                  </button>
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                    <h5 className="font-medium text-yellow-800 mb-2">Low Stock Alert</h5>
                    <p className="text-sm text-yellow-700">
                      {inventoryData.filter(item => item.quantity <= item.threshold).length} items need restocking
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Order & Delivery Tab */}
        {activeTab === 'delivery' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <StatCard
                title="Total Deliveries"
                value={deliveryData.reduce((sum, week) => sum + week.delivered, 0).toString()}
                subtitle="Last 6 weeks"
                icon={Truck}
                color="#8B5CF6"
              />
              <StatCard
                title="On-Time Rate"
                value="92%"
                subtitle="Delivery performance"
                icon={Clock}
                color="#10B981"
              />
              <StatCard
                title="In Transit"
                value="14"
                subtitle="Currently delivering"
                icon={MapPin}
                color="#F59E0B"
              />
              <StatCard
                title="Scheduled Today"
                value="8"
                subtitle="Upcoming deliveries"
                icon={Calendar}
                color="#06B6D4"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Delivery Performance Trends</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={deliveryData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="delivered" stroke="#10B981" strokeWidth={3} dot={{ fill: '#10B981', strokeWidth: 2, r: 6 }} name="Delivered" />
                    <Line type="monotone" dataKey="scheduled" stroke="#06B6D4" strokeWidth={3} dot={{ fill: '#06B6D4', strokeWidth: 2, r: 6 }} name="Scheduled" />
                    <Line type="monotone" dataKey="onTime" stroke="#8B5CF6" strokeWidth={3} dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 6 }} name="On Time" />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Delivery Status Overview</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Delivered', value: 342, color: '#10B981' },
                        { name: 'In Transit', value: 14, color: '#F59E0B' },
                        { name: 'Scheduled', value: 23, color: '#06B6D4' },
                        { name: 'Delayed', value: 8, color: '#EF4444' }
                      ]}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      stroke="none"
                    >
                      {[
                        { name: 'Delivered', value: 342, color: '#10B981' },
                        { name: 'In Transit', value: 14, color: '#F59E0B' },
                        { name: 'Scheduled', value: 23, color: '#06B6D4' },
                        { name: 'Delayed', value: 8, color: '#EF4444' }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: 'none', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Recent Delivery Activity</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Delivery ID</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Items</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Destination</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Driver</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Estimated Time</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Actual Time</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recentDeliveries.map((delivery, index) => (
                      <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{delivery.id}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{delivery.items}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                            {delivery.destination}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{delivery.driver}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(delivery.status)}`}>
                            {getStatusIcon(delivery.status)}
                            <span className="ml-1">{delivery.status}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{delivery.estimatedTime}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {delivery.actualTime !== '-' ? (
                            <span className="text-green-600 font-medium">{delivery.actualTime}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{delivery.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Today's Schedule</h4>
                <div className="space-y-3">
                  {[
                    { time: '09:00 AM', item: 'Cement delivery to Site A', status: 'Completed' },
                    { time: '11:30 AM', item: 'Steel rods to Office Building', status: 'In Progress' },
                    { time: '02:00 PM', item: 'Bricks to Residential Complex', status: 'Scheduled' },
                    { time: '04:30 PM', item: 'Equipment to Highway Project', status: 'Scheduled' }
                  ].map((schedule, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50/50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{schedule.item}</p>
                        <p className="text-xs text-gray-600">{schedule.time}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        schedule.status === 'Completed' ? 'bg-green-100 text-green-700' :
                        schedule.status === 'In Progress' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {schedule.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Driver Performance</h4>
                <div className="space-y-4">
                  {[
                    { name: 'Carlos Rodriguez', deliveries: 24, onTime: 96, rating: 4.8 },
                    { name: 'James Wilson', deliveries: 19, onTime: 89, rating: 4.6 },
                    { name: 'Maria Santos', deliveries: 22, onTime: 91, rating: 4.7 },
                    { name: 'Robert Lee', deliveries: 18, onTime: 94, rating: 4.9 }
                  ].map((driver, index) => (
                    <div key={index} className="p-4 bg-gray-50/50 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <p className="font-medium text-gray-900">{driver.name}</p>
                        <span className="text-yellow-500">★ {driver.rating}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>{driver.deliveries} deliveries</span>
                        <span>{driver.onTime}% on-time</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200/50">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h4>
                <div className="space-y-3">
                  <button className="w-full p-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-105 text-sm">
                    Schedule New Delivery
                  </button>
                  <button className="w-full p-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 transform hover:scale-105 text-sm">
                    Track All Vehicles
                  </button>
                  <button className="w-full p-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105 text-sm">
                    Generate Delivery Report
                  </button>
                  <button className="w-full p-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl hover:from-yellow-600 hover:to-orange-600 transition-all duration-200 transform hover:scale-105 text-sm">
                    Optimize Routes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
    </LayoutWrapper>
  );
};

export default InfraDashboard;